import React, { useEffect, useRef, useState } from "react";
import { FaExternalLinkAlt, FaInfoCircle, FaMapMarkedAlt, FaMapMarkerAlt } from "react-icons/fa";
import { GOOGLE_MAPS_API_KEY, loadPlacesLibrary, mapsSearchUrl, parsePlace } from "../utils/googleMaps";
import "../assets/css/AddressSearch.css";

const AUTOFILL_INFO = "Pick a Google result to fill the address fields below.";
const FALLBACK_INFO =
  "Google autofill is not configured (REACT_APP_GOOGLE_MAPS_API_KEY). Search opens Google Maps in a new tab; copy the address into the fields below.";

/**
 * Google Places search box. On selection calls onSelect(parsedAddress) — see parsePlace().
 * Without an API key (or if Google fails to load) it falls back to opening a Google Maps search.
 */
function AddressSearch({ onSelect, viewParts = [], label = "Search address on Google" }) {
  const hostRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(!GOOGLE_MAPS_API_KEY);
  const [query, setQuery] = useState("");

  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return undefined;
    let element = null;
    let cancelled = false;

    loadPlacesLibrary()
      .then(({ PlaceAutocompleteElement }) => {
        if (cancelled || !hostRef.current) return;
        element = new PlaceAutocompleteElement();
        element.setAttribute("placeholder", "Search business or address");
        element.addEventListener("gmp-select", async ({ placePrediction }) => {
          try {
            const place = placePrediction.toPlace();
            await place.fetchFields({
              fields: ["displayName", "formattedAddress", "addressComponents", "googleMapsURI", "types"],
            });
            onSelectRef.current?.(parsePlace(place));
          } catch (err) {
            console.error("Place details failed:", err);
            setError("Could not read that place. Try another result.");
          }
        });
        hostRef.current.appendChild(element);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
      if (element) element.remove();
    };
  }, []);

  const openMapsSearch = () => {
    const url = mapsSearchUrl([query]);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const viewUrl = mapsSearchUrl(viewParts) || mapsSearchUrl([query]);
  const mapButton = (
    <a
      className={`address-search__map${viewUrl ? "" : " is-disabled"}`}
      href={viewUrl || undefined}
      target="_blank"
      rel="noopener noreferrer"
      title={viewUrl ? "View on Google Maps" : "Enter an address to view it on Google Maps"}
      aria-label="View on Google Maps"
      onClick={(e) => {
        if (!viewUrl) e.preventDefault();
      }}
    >
      <FaMapMarkedAlt />
    </a>
  );

  const info = unavailable ? FALLBACK_INFO : AUTOFILL_INFO;

  return (
    <div className="form-group address-search">
      <label>
        <FaMapMarkerAlt className="address-search__icon" /> {label}
        <FaInfoCircle className="services-info-icon address-search__info" title={info} aria-label={info} />
      </label>
      {unavailable ? (
        <div className="address-search__fallback">
          <input
            type="text"
            value={query}
            placeholder="Search business or address"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                openMapsSearch();
              }
            }}
          />
          <button
            type="button"
            className="address-search__btn"
            onClick={openMapsSearch}
            disabled={!query.trim()}
          >
            <FaExternalLinkAlt /> Search
          </button>
          {mapButton}
        </div>
      ) : (
        <div className="address-search__fallback">
          <div ref={hostRef} className="address-search__host" />
          {mapButton}
        </div>
      )}
      {error ? <small className="form-hint address-search__error">{error}</small> : null}
    </div>
  );
}

export default AddressSearch;
